import * as THREE from 'three';
import type { Number3 } from '../types';

export type DraggingInfo = {
  startPosition: Number3;
  position: Number3;
  groupWorldMatrix: THREE.Matrix4;
  groupWorldMatrixInverse: THREE.Matrix4;
};

export interface DraggingProps {
  type: 'line' | 'plane';
  event: PointerEvent;
  onDragging: (event: PointerEvent, info: DraggingInfo) => void;
  onStop: () => void;
  startPosition: Number3;
  // direction for line, plane normal for plane
  draggingDirection: Number3;
  draggingStep: Number3;
  localGroup: THREE.Group;
}

// finds the point on rayA cosest to rayB
const findRayClosestPoint = (rayA: THREE.Ray, rayB: THREE.Ray, outPtA: THREE.Vector3) => {
  // https://stackoverflow.com/questions/58151978/threejs-how-to-calculate-the-closest-point-on-a-three-ray-to-another-three-ray

  const Nv = rayA.direction.clone().cross(rayB.direction);
  // const Na = rayA.direction.clone().cross(Nv).normalize();
  const Nb = rayB.direction.clone().cross(Nv).normalize();

  const Da = rayA.direction.clone().normalize();
  // const Db = rayB.direction.clone().normalize();

  const da = rayB.origin.clone().sub(rayA.origin).dot(Nb) / Da.dot(Nb);
  // const db = rayA.origin.clone().sub(rayB.origin).dot(Na) / Db.dot(Na);

  // const ptA = rayA.origin.clone().add(Da.multiplyScalar(da));
  // const ptB = rayB.origin.clone().add(Db.multiplyScalar(db));

  outPtA.copy(Da).multiplyScalar(da).add(rayA.origin);

  // return ptA;
};

const snapVector3ToGrid = (vec: THREE.Vector3, gridStep: THREE.Vector3) => {
  const snapToGrid = (coord: number, step: number) => {
    return step === 0 ? coord : Math.round(coord / step) * step;
  };
  vec.setX(snapToGrid(vec.x, gridStep.x));
  vec.setY(snapToGrid(vec.y, gridStep.y));
  vec.setZ(snapToGrid(vec.z, gridStep.z));
};

/**
 * DraggingHelper implements dragging of an 3d object.
 */
export class DraggingHelper {
  private domElement: HTMLCanvasElement;

  private enableControls: (isEnabled: boolean) => void;

  private draggingProps: DraggingProps | undefined;

  private localGroup: THREE.Group | undefined;
  private groupWorldMatrix = new THREE.Matrix4();
  private groupWorldMatrixInverse = new THREE.Matrix4();

  private position = new THREE.Vector3();
  private prevPosition = new THREE.Vector3();

  private step = new THREE.Vector3();
  private stepOrigin = new THREE.Vector3();

  private pointer2d = new THREE.Vector2();
  private raycaster = new THREE.Raycaster();

  private localDraggingRay = new THREE.Ray();
  private worldDraggingRay = new THREE.Ray();

  private camera: THREE.OrthographicCamera | THREE.PerspectiveCamera;

  private isDraggingActive: boolean;

  private onPointerMoveFunc: (event: PointerEvent) => void;
  private onPointerCancelFunc: (event: PointerEvent) => void;

  constructor(
    domElement: HTMLCanvasElement,
    camera: THREE.OrthographicCamera | THREE.PerspectiveCamera,
    enableControls: (isEnabled: boolean) => void
  ) {
    this.enableControls = enableControls;
    this.domElement = domElement;
    this.camera = camera;
    this.isDraggingActive = false;
    this.onPointerMoveFunc = this.onPointerMove.bind(this);
    this.onPointerCancelFunc = this.onPointerCancel.bind(this);
  }

  private activate() {
    this.domElement.addEventListener('pointermove', this.onPointerMoveFunc);
    this.domElement.addEventListener('pointerup', this.onPointerCancelFunc);
    this.domElement.addEventListener('pointerleave', this.onPointerCancelFunc);
    this.enableControls(false);
    this.isDraggingActive = true;
  }

  private deactivate() {
    this.domElement.removeEventListener('pointermove', this.onPointerMoveFunc);
    this.domElement.removeEventListener('pointerup', this.onPointerCancelFunc);
    this.domElement.removeEventListener('pointerleave', this.onPointerCancelFunc);
    this.enableControls(true);
    this.isDraggingActive = false;
  }

  private getPointer(event: PointerEvent) {
    const { clientX, clientY, button } = event;
    const { pointer2d, domElement } = this;
    if (domElement.ownerDocument.pointerLockElement) {
      pointer2d.set(0, 0);
    } else {
      const { left, top, width, height } = this.domElement.getBoundingClientRect();
      const x = ((clientX - left) / width) * 2 - 1;
      const y = (-(clientY - top) / height) * 2 + 1;
      pointer2d.set(x, y);
    }
    return { pointer2d, button };
  }

  private updatePosition(event: PointerEvent) {
    const {
      camera,
      raycaster,
      worldDraggingRay,
      draggingProps,
      position,
      prevPosition,
      step,
      stepOrigin,
      localGroup,
      groupWorldMatrix,
      groupWorldMatrixInverse,
    } = this;

    if (!draggingProps) return;
    if (!localGroup) return;

    const { pointer2d } = this.getPointer(event);

    raycaster.setFromCamera(pointer2d, camera);
    findRayClosestPoint(worldDraggingRay, raycaster.ray, position);

    const localPosition = position.clone();
    localPosition.applyMatrix4(groupWorldMatrixInverse);

    localPosition.sub(stepOrigin);
    snapVector3ToGrid(localPosition, step);
    localPosition.add(stepOrigin);

    // snapped position from local
    position.copy(localPosition);
    position.applyMatrix4(groupWorldMatrix);

    const { startPosition, onDragging } = draggingProps;

    if (!prevPosition.equals(position)) {
      prevPosition.copy(position);

      onDragging(event, {
        startPosition,
        position: localPosition.toArray(),
        groupWorldMatrix,
        groupWorldMatrixInverse,
      });
    }
  }

  startDragging(props: DraggingProps) {
    if (props.type !== 'line') {
      throw new Error(`Unsupported dragging type '${props.type}'`);
    }

    this.draggingProps = props;

    const { startPosition, draggingDirection, draggingStep, localGroup } = props;
    const {
      position,
      prevPosition,
      localDraggingRay,
      worldDraggingRay,
      step,
      stepOrigin,
      groupWorldMatrix,
      groupWorldMatrixInverse,
    } = this;

    this.localGroup = localGroup;

    localGroup.updateWorldMatrix(true, false);
    groupWorldMatrix.copy(localGroup.matrixWorld);
    groupWorldMatrixInverse.copy(localGroup.matrixWorld).invert();

    position.set(...startPosition);
    prevPosition.copy(position);

    stepOrigin.copy(position);

    localDraggingRay.origin.set(...draggingDirection);
    localDraggingRay.origin.multiplyScalar(-100_000);
    localDraggingRay.direction.set(...draggingDirection);

    worldDraggingRay.copy(localDraggingRay);
    worldDraggingRay.applyMatrix4(groupWorldMatrix);

    step.set(...draggingStep);

    this.activate();
  }

  private onPointerMove(event: PointerEvent) {
    this.updatePosition(event);
  }

  private onPointerCancel(event: PointerEvent) {
    this.updatePosition(event);

    this.draggingProps?.onStop();
    this.draggingProps = undefined;

    if (this.isDraggingActive) {
      this.deactivate();
    }
  }
}

export default DraggingHelper;
